import * as React from "react";
import {FC, useCallback, useContext, useEffect, useMemo} from "react";
import {observer} from "mobx-react";
import styles from "./Service.module.scss";
import {MaintenanceDTO, MaintenanceStatus, SortingItem} from "../../Models/ApiModels";
import {AppStoreContext} from "../../Stores/AppStore";
import {AjaxContentType, ApiService} from "../../Services";
import {ApiURL} from "../../AppConstants";
import DateTimeService from "../../Services/DateTimeService";
import {Button} from "devextreme-react";
import {NoDataHint, SelectMachineHint} from "./components/Hints";
import {GetOptionsFromList, ListFilterSorting} from "./components/ListFilterSorting";

const Logs = observer(() => {
    const context = useContext(AppStoreContext);
    const {logs, loadLogs, globalLoader, filtersStore, logsList} = context;

    useEffect(() => {
        void loadLogs();
    }, [filtersStore.selected, loadLogs]);


    const taskSortingItems: SortingItem[] = useMemo(() => [
        {key: 'description', label: 'Description'},
        {key: 'due', label: 'Due'},
        {key: 'time', label: 'Time'},
    ], []);

    const taskFilterItems: SortingItem[] = useMemo(() => [
        {key: 'status', label: 'Status', options: GetOptionsFromList(logs?.map(t => t.status))},
        {
            key: 'type',
            label: 'Type',
            options: [{label: 'Scheduled', value: 'isScheduled'}, {label: 'Data Based', value: 'dataBased'}]
        },
        {key: 'isRecurring', label: 'Recurring', options: [{label: 'Yes', value: true}, {label: 'No', value: false}]},
        {key: 'isAlmostDue', label: 'Almost Due', options: [{label: 'Yes', value: true}, {label: 'No', value: false}]},
    ], [logs]);

    const _onExport = useCallback(async () => {
        const params = {
            organizationId: filtersStore.selected.customer,
            siteId: [filtersStore.selected.site],
            deviceId: [filtersStore.selected.plant]
        };
        const {data} = await ApiService.getData<MaintenanceDTO[]>(ApiURL.MaintenanceTasks, {
            ...params,
            status: MaintenanceStatus.Completed
        }, {
            contentType: AjaxContentType.CSV,
            completion: globalLoader
        });
        const href = URL.createObjectURL(data);
        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', `Log Entries ${DateTimeService.formatLocal(DateTimeService.now())}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
    }, [filtersStore, globalLoader]);

    if (!filtersStore.selected.plant) {
        return <SelectMachineHint/>;
    }

    return (
        <>
            <div className={styles.title}>
                Log Entries
                <div className={styles.mlAuto}>
                    <Button className={styles.action} onClick={_onExport}>
                        Export Check List
                    </Button>
                </div>
            </div>
            <ListFilterSorting
                sortingItems={taskSortingItems}
                filterItems={taskFilterItems}
            />
            <div className={styles.list}>
                {logsList !== null && !logsList.length && <NoDataHint message="Empty log data"/>}
                {!!logsList?.length &&
                    <div className={styles.listContainer}>
                        {logsList.map(task => <LogItem key={`${task.taskId}_${task.cycle}`} task={task}/>)}
                    </div>
                }
            </div>
        </>
    );
});

const LogItem: FC<{ task: MaintenanceDTO }> = observer(({task}) => {
    const {time, description, userLog} = task;
    const value = task.value || 0;
    const initialValue = task.initialValue || 0;
    const valueDelta = task.valueDelta || 0;
    const due = task.due || 0;
    const currentValue = Math.round(value - initialValue);
    const label = task.timeDelta ? `${Math.round(due * 100)}%` : `${currentValue}/${valueDelta || 0}`;

    return (
        <div className={styles.listItem}>
            <div className={styles.listDesc}>{description}</div>
            <div className={styles.listComment}>
                {userLog?.message??''}
                <br/>
                <b>{DateTimeService.format(time)} by <i title={userLog.id}>{userLog?.displayName ? userLog.displayName : 'Unknown'}</i></b>
            </div>
            <div className={styles.listBar}>
                <div className={styles.listValue}>{label}</div>
                <div className={`${styles.listProgress} ${due >= 1 ? styles.full : ''}`}
                     style={{width: `${(due >= 1 ? 1 : due) * 100}%`}}></div>
            </div>
        </div>
    );
});

export default Logs;